import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2 } from "lucide-react";
import { adminUploadImage } from "../lib/api";

// A drag-drop + click file input that uploads to backend and returns a URL
const ImageUploader = ({ onUploaded, label = "Téléverser une image", testid = "image-upload" }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Format non supporté (JPG, PNG, WEBP)");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image trop volumineuse (5 MB max)");
      return;
    }
    setUploading(true);
    try {
      const res = await adminUploadImage(file);
      // Store as RELATIVE path so images stay portable across preview/prod hosts.
      // Consumers resolve to absolute at render time via `resolveMediaUrl`.
      onUploaded(res.url);
      toast.success("Image téléversée");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      data-testid={testid}
      onClick={() => !uploading && inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const f = e.dataTransfer?.files?.[0];
        handleFile(f);
      }}
      className={`cursor-pointer border border-dashed p-4 flex items-center gap-3 transition-colors ${
        dragOver ? "border-[color:var(--gold)] bg-[color:var(--gold)]/5" : "border-white/20 hover:border-white/40"
      } ${uploading ? "opacity-60 pointer-events-none" : ""}`}
    >
      {uploading ? <Loader2 size={16} className="text-[color:var(--gold)] animate-spin" /> : <Upload size={16} className="text-[color:var(--gold)]" />}
      <span className="label-caps text-[10px] text-white/80">
        {uploading ? "Téléversement..." : label}
      </span>
      <span className="text-white/40 text-xs ml-auto hidden md:inline">JPG · PNG · WEBP · 5 MB max</span>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={(e) => handleFile(e.target.files?.[0])}
        className="hidden"
      />
    </div>
  );
};

export default ImageUploader;
